<template>
  <md-dialog :md-active.sync="active" class="password-change-modal">
    <md-dialog-title>
      <div class="dialog-header">
        <span>Change Password</span>
        <badge v-if="passwordStrength" :type="passwordStrengthColorType">
          {{ passwordStrengthLabel }}
        </badge>
      </div>
    </md-dialog-title>

    <md-dialog-content>
      <div class="modal-content">
        <form @submit.prevent="submit" ref="passwordForm">
          <!-- Current Password -->
          <md-field :class="{'md-error': errors.current_password}">
            <label>Current Password</label>
            <md-input
                v-model="form.current_password"
                type="password"
                required
                @input="clearError('current_password')"
            />
            <span class="md-error" v-if="errors.current_password">
              {{ errors.current_password[0] }}
            </span>
          </md-field>

          <!-- New Password -->
          <md-field :class="{'md-error': apiValidationErrors.new_password}">
            <label>New Password</label>
            <md-input
                v-model="form.new_password"
                type="password"
                required
                @input="onPasswordChange"
                @focus="showPasswordRequirements = true"
                @blur="showPasswordRequirements = false"
            />
            <span class="md-error" v-if="errors.new_password">
              {{ errors.new_password[0] }}
            </span>
          </md-field>
          <validation-error :errors="apiValidationErrors.new_password"/>

          <!-- Password Strength Meter -->
          <div v-if="form.new_password" class="password-meter">
            <div class="meter-bar">
              <div
                  class="meter-fill"
                  :style="{ width: passwordScore + '%', background: passwordStrengthColor }"
              ></div>
            </div>
            <div class="meter-label">
              <span :style="{ color: passwordStrengthColor }">
                {{ passwordStrengthLabel }}
              </span>
              <span class="score">{{ passwordScore }}%</span>
            </div>
          </div>

          <!-- Password Requirements -->
          <div v-if="showPasswordRequirements || form.new_password" class="password-requirements">
            <h5>Password Requirements:</h5>
            <ul>
              <li :class="{'met': requirements.length}">
                <md-icon>{{ requirements.length ? 'check_circle' : 'radio_button_unchecked' }}</md-icon>
                At least 8 characters long
              </li>
              <li :class="{'met': requirements.uppercase}">
                <md-icon>{{ requirements.uppercase ? 'check_circle' : 'radio_button_unchecked' }}</md-icon>
                At least one uppercase letter
              </li>
              <li :class="{'met': requirements.lowercase}">
                <md-icon>{{ requirements.lowercase ? 'check_circle' : 'radio_button_unchecked' }}</md-icon>
                At least one lowercase letter
              </li>
              <li :class="{'met': requirements.number}">
                <md-icon>{{ requirements.number ? 'check_circle' : 'radio_button_unchecked' }}</md-icon>
                At least one number
              </li>
              <li :class="{'met': requirements.special}">
                <md-icon>{{ requirements.special ? 'check_circle' : 'radio_button_unchecked' }}</md-icon>
                At least one special character
              </li>
              <li :class="{'met': requirements.no_repeating}">
                <md-icon>{{ requirements.no_repeating ? 'check_circle' : 'radio_button_unchecked' }}</md-icon>
                No three or more repeating characters
              </li>
              <li :class="{'met': requirements.no_common_pattern}">
                <md-icon>{{ requirements.no_common_pattern ? 'check_circle' : 'radio_button_unchecked' }}</md-icon>
                Not a common or easily guessable password
              </li>
            </ul>
          </div>

          <!-- Confirm Password -->
          <md-field :class="{'md-error': errors.confirm_password || passwordMismatch}">
            <label>Confirm Password</label>
            <md-input
                v-model="form.confirm_password"
                type="password"
                required
                @input="clearError('confirm_password')"
            />
            <span class="md-error" v-if="passwordMismatch">
              Passwords do not match
            </span>
            <span class="md-error" v-if="errors.confirm_password">
              {{ errors.confirm_password[0] }}
            </span>
          </md-field>

          <!-- Password History Warning -->
          <div v-if="form.new_password && passwordInHistory" class="history-warning">
            <md-icon>warning</md-icon>
            <span>You cannot use any of your last 5 passwords. Please choose a different password.</span>
          </div>

          <!-- Server Errors -->
          <div v-if="serverError" class="server-error">
            <md-icon>error</md-icon>
            <span>{{ serverError }}</span>
          </div>
        </form>
      </div>
    </md-dialog-content>

    <md-dialog-actions>
      <md-button
          class="md-primary"
          @click="submit"
          :disabled="isSubmitting"
      >
        {{ isSubmitting ? 'Updating...' : 'Update Password' }}
      </md-button>
      <md-button class="md-danger" @click="close">Cancel</md-button>
    </md-dialog-actions>
  </md-dialog>
</template>

<script>
import {mapActions} from 'vuex';

import formMixin from '@/mixins/form-mixin';
import Badge from '@/components/Badge.vue';
import debounce from 'lodash/debounce';
import ValidationError from "@/components/ValidationError.vue";

export default {
  name: 'PasswordChangeModal',
  components: {
    ValidationError,
    Badge: Badge
  },
  mixins: [formMixin],
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isSubmitting: false,
      showPasswordRequirements: false,
      passwordScore: 0,
      passwordStrength: null,
      passwordStrengthLabel: '',
      passwordStrengthColor: '',
      passwordStrengthColorType: 'info',
      passwordInHistory: false,
      passwordMismatch: false,
      serverError: null,
      errors: {},
      form: {
        current_password: '',
        new_password: '',
        confirm_password: ''
      },
      requirements: {
        length: false,
        uppercase: false,
        lowercase: false,
        number: false,
        special: false,
        no_repeating: false,
        no_common_pattern: false
      }
    };
  },
  computed: {
    active: {
      get: function () {
        return this.value;
      },
      set: function (value) {
        this.$emit('input', value);
      }
    },
    isFormValid: function () {
      return this.form.current_password &&
          this.form.new_password &&
          this.form.confirm_password &&
          this.passwordScore >= 80 &&
          !this.passwordMismatch &&
          !this.passwordInHistory &&
          Object.keys(this.errors).length === 0;
    }
  },
  watch: {
    'form.confirm_password': function (val) {
      this.passwordMismatch = val !== this.form.new_password && val.length > 0;
    }
  },
  methods: {
    ...mapActions('passwordModule', ['changePassword', 'checkPasswordStrength']),

    onPasswordChange: debounce(function () {
      this.checkPasswordStrengthDebounced();
    }, 300),

    async checkPasswordStrengthDebounced() {
      if (!this.form.new_password || this.form.new_password.length < 1) {
        return;
      }

      try {
        const response = await this.checkPasswordStrength({
          password: this.form.new_password
        });
        const data = response.data || response;
        console.log(data.strength.color);
        this.passwordScore = data.score || 0;
        this.passwordStrength = data.strength;
        this.passwordStrengthLabel = data.strength ? data.strength.label : 'Weak';
        this.passwordStrengthColor = data.strength ? data.strength.color : '#f44336';
        this.passwordStrengthColorType = data.strength ? data.strength.badge : 'danger';
        this.requirements = data.requirements || {};
        this.passwordInHistory = false;
      } catch (error) {
        console.error('Password strength check failed:', error);
      }
    },

    async submit() {
      this.serverError = null;
      this.errors = {};
      this.passwordMismatch = false;

      // Validate confirm password
      if (this.form.new_password !== this.form.confirm_password) {
        this.passwordMismatch = true;
        this.$store.dispatch('alerts/error', 'Passwords do not match');
        return;
      }

      // Validate password strength
      if (this.passwordScore < 80) {
        this.$store.dispatch('alerts/error', 'Please choose a stronger password');
        return;
      }

      this.isSubmitting = true;

      try {
        await this.changePassword({
          current_password: this.form.current_password,
          new_password: this.form.new_password,
          confirm_password: this.form.confirm_password
        });

        this.notifySuccess('Password updated successfully');
        this.close();
        this.$emit('success');

      } catch (error) {
        console.error('Password change error:', error);

        this.isSubmitting = false;
        if (error && error.response && error.response.data && error.response.data.errors) {
          this.errors = error.response.data.errors;
          this.serverError = error.response.data.message;
          this.setApiValidation(error.response.data.errors);
          console.log(this.apiValidationErrors.new_password);
        } else if (error && error.response && error.response.data && error.response.data.message) {
          this.serverError = error.response.data.message;
        } else {
          this.serverError = 'Failed to update password. Please try again.';
        }
        this.notifyException(error);
        console.log(this.errors)
      } finally {
        this.isSubmitting = false;
      }
    },

    clearError(field) {
      if (this.errors[field]) {
        delete this.errors[field];
      }
    },

    close() {
      this.active = false;
      this.resetForm();
    },

    resetForm() {
      this.form = {
        current_password: '',
        new_password: '',
        confirm_password: ''
      };
      this.errors = {};
      this.serverError = null;
      this.passwordScore = 0;
      this.passwordStrength = null;
      this.passwordInHistory = false;
      this.passwordMismatch = false;
    },

    notifySuccess(message) {
      this.$store.dispatch('alerts/success', message);
    },

    notifyError(message) {
      this.$store.dispatch('alerts/error', message);
    }
  }
};
</script>

<style scoped>
.password-change-modal .md-dialog {
  max-width: 550px;
  width: 95%;
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.modal-content {
  padding: 10px 0;
}

.password-meter {
  margin: 15px 0;
}

.meter-bar {
  width: 100%;
  height: 8px;
  background: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.meter-fill {
  height: 100%;
  transition: width 0.3s ease;
}

.meter-label {
  display: flex;
  justify-content: space-between;
  margin-top: 5px;
  font-size: 13px;
}

.score {
  color: #999;
}

.password-requirements {
  background: #f5f5f5;
  padding: 15px;
  border-radius: 6px;
  margin: 15px 0;
}

.password-requirements h5 {
  margin: 0 0 10px 0;
  font-size: 13px;
  color: #555;
}

.password-requirements ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.password-requirements li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 12px;
  color: #999;
}

.password-requirements li.met {
  color: #4caf50;
}

.password-requirements li md-icon {
  font-size: 18px;
  width: 18px;
  height: 18px;
}

.history-warning {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  background: #fff3e0;
  border-radius: 4px;
  margin: 10px 0;
  color: #e65100;
  font-size: 13px;
}

.server-error {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  background: #ffebee;
  border-radius: 4px;
  margin: 10px 0;
  color: #c62828;
  font-size: 13px;
}

.server-error md-icon {
  color: #c62828;
}

.md-field.md-error .md-input {
  border-color: #f44336;
}
</style>